import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { DeliverableImportTypes } from '@mono/proto-clients';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useChatFeedback } from '../../ChatFeedbackContext';
import { deliverableImportFeedback } from './card';
import { useDeliverableImportClient } from './client';
import { workstreamLabel } from './shared';
import { deliverableImportCommitResultKey, invalidateDeliverableImportReview } from './useReview';
const { DeliverableImportAction, DeliverableImportDecision } = DeliverableImportTypes;
const DECISION_ROWS = [
    {
        label: 'Approved',
        count: (p)=>p.filter((x)=>x.decision === DeliverableImportDecision.APPROVED).length
    },
    {
        label: 'Edited',
        count: (p)=>p.filter((x)=>x.decision === DeliverableImportDecision.EDITED_APPROVED).length
    },
    {
        label: 'Rejected',
        count: (p)=>p.filter((x)=>x.decision === DeliverableImportDecision.REJECTED).length
    },
    {
        label: 'Pending',
        count: (p)=>p.filter((x)=>x.decision === DeliverableImportDecision.PENDING).length
    },
    {
        label: 'Already Matches',
        count: (p)=>p.filter((x)=>x.action === DeliverableImportAction.ALREADY_MATCHES).length
    },
    {
        label: 'Needs Clarification',
        count: (p)=>p.filter((x)=>x.action === DeliverableImportAction.NEEDS_CLARIFICATION).length
    }
];
/**
 * WORKSTREAM_SUMMARY body, scoped to `payload.workstreamKey`. The set of
 * proposal ids counted into "ready to commit" is captured here, at render
 * time, from `review.proposals` — the exact snapshot on screen — and that
 * frozen list is what gets sent as `expected_proposal_ids`, never a
 * recomputation after some later fetch.
 */ export function WorkstreamSummaryCard({ payload, review }) {
    const client = useDeliverableImportClient();
    const queryClient = useQueryClient();
    const sendFeedback = useChatFeedback();
    const [precondition, setPrecondition] = useState(false);
    const [commitError, setCommitError] = useState(null);
    const [committed, setCommitted] = useState(false);
    const ws = review.workstreams.find((w)=>w.key === payload.workstreamKey);
    const proposals = review.proposals;
    const commit = useMutation({
        mutationFn: (expectedProposalIds)=>client.commitDeliverableImportWorkstream({
                programId: payload.programId,
                changeSetId: payload.changeSetId,
                workstreamKey: payload.workstreamKey,
                expectedProposalIds
            })
    });
    if (!ws) {
        return /*#__PURE__*/ _jsx("div", {
            className: "mt-2 text-sm text-gray-500",
            children: "This workstream is no longer part of the staged import."
        });
    }
    const label = workstreamLabel(ws);
    const approvedSet = proposals.filter((p)=>p.decision === DeliverableImportDecision.APPROVED || p.decision === DeliverableImportDecision.EDITED_APPROVED);
    const approvedIds = approvedSet.map((p)=>p.id);
    const creates = approvedSet.filter((p)=>p.action === DeliverableImportAction.CREATE).length;
    const updates = approvedSet.filter((p)=>p.action === DeliverableImportAction.UPDATE).length;
    const readyCount = approvedSet.length;
    const rejectedCount = proposals.filter((p)=>p.decision === DeliverableImportDecision.REJECTED).length;
    const pendingCount = proposals.filter((p)=>p.decision === DeliverableImportDecision.PENDING).length;
    const alreadyMatchesCount = proposals.filter((p)=>p.action === DeliverableImportAction.ALREADY_MATCHES).length;
    const disabledReason = readyCount > 0 ? null : pendingCount > 0 ? `No proposals are approved yet — ${pendingCount} still need review. Go back and approve at least one before committing.` : 'Nothing to commit — every proposal in this workstream is already rejected or already matches program data.';
    const handleCommit = ()=>{
        if (commit.isPending || readyCount === 0) return;
        setCommitError(null);
        setPrecondition(false);
        commit.mutate(approvedIds, {
            onSuccess: async (response)=>{
                queryClient.setQueryData(deliverableImportCommitResultKey(payload.programId, payload.changeSetId, ws.key), response);
                setCommitted(true);
                await invalidateDeliverableImportReview(queryClient, payload.programId, payload.changeSetId);
                sendFeedback(deliverableImportFeedback({
                    event: 'committed',
                    changeSetId: payload.changeSetId,
                    workstreamKey: ws.key,
                    text: `Program Lead committed workstream "${label}": ${response.created} created, ${response.updated} updated, ${response.failed} failed.`
                }));
            },
            onError: async (error)=>{
                if (error instanceof ConnectError && error.code === Code.FailedPrecondition) {
                    // Silent on purpose: this path refetches and immediately re-presents
                    // the commit button with fresh numbers, so the user is still
                    // mid-action and the agent should stay waiting on this card.
                    await invalidateDeliverableImportReview(queryClient, payload.programId, payload.changeSetId);
                    setPrecondition(true);
                    return;
                }
                const message = error instanceof Error ? error.message : String(error);
                setCommitError(message);
                sendFeedback(deliverableImportFeedback({
                    event: 'committed',
                    changeSetId: payload.changeSetId,
                    workstreamKey: ws.key,
                    text: `The commit attempt for workstream "${label}" failed: ${message} Nothing was written — no deliverables were created or updated, so the workstream is still awaiting commit.`
                }));
            }
        });
    };
    const handleReviewApproved = ()=>{
        sendFeedback(deliverableImportFeedback({
            event: 'navigate',
            changeSetId: payload.changeSetId,
            workstreamKey: ws.key,
            text: `Program Lead wants to review the ${readyCount} approved change${readyCount === 1 ? '' : 's'} in "${label}" before committing.`
        }));
    };
    const handleReturnToPending = ()=>{
        sendFeedback(deliverableImportFeedback({
            event: 'navigate',
            changeSetId: payload.changeSetId,
            workstreamKey: ws.key,
            text: `Program Lead returned to the ${pendingCount} pending change${pendingCount === 1 ? '' : 's'} in "${label}".`
        }));
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-2 space-y-3 text-sm text-gray-700",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "font-medium text-gray-900",
                children: label
            }),
            /*#__PURE__*/ _jsxs("table", {
                className: "w-full text-xs border rounded-md",
                children: [
                    /*#__PURE__*/ _jsx("thead", {
                        className: "bg-gray-50 text-gray-500",
                        children: /*#__PURE__*/ _jsxs("tr", {
                            children: [
                                /*#__PURE__*/ _jsx("th", {
                                    className: "text-left font-medium px-2 py-1",
                                    children: "Decision"
                                }),
                                /*#__PURE__*/ _jsx("th", {
                                    className: "text-right font-medium px-2 py-1",
                                    children: "Count"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("tbody", {
                        children: DECISION_ROWS.map((row)=>/*#__PURE__*/ _jsxs("tr", {
                                className: "border-t",
                                children: [
                                    /*#__PURE__*/ _jsx("td", {
                                        className: "px-2 py-1 text-gray-900",
                                        children: row.label
                                    }),
                                    /*#__PURE__*/ _jsx("td", {
                                        className: "px-2 py-1 text-right",
                                        children: row.count(proposals)
                                    })
                                ]
                            }, row.label))
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs text-gray-700",
                children: [
                    "Approved changes ready to commit: ",
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-semibold",
                        children: readyCount
                    }),
                    " (",
                    creates,
                    " create",
                    creates === 1 ? '' : 's',
                    ", ",
                    updates,
                    " update",
                    updates === 1 ? '' : 's',
                    ")"
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs text-gray-500",
                children: [
                    "Will not be changed: ",
                    rejectedCount,
                    " rejected, ",
                    pendingCount,
                    " still pending,",
                    ' ',
                    alreadyMatchesCount,
                    " already matching."
                ]
            }),
            precondition && /*#__PURE__*/ _jsxs("div", {
                className: "border-l-2 border-amber-300 pl-2 text-xs text-gray-700 space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: "The approved set for this workstream changed since this card was loaded. Here are the current numbers — review them and commit again."
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-gray-500",
                        children: [
                            "Now ready to commit: ",
                            readyCount,
                            " (",
                            creates,
                            " creates, ",
                            updates,
                            " updates)."
                        ]
                    })
                ]
            }),
            commitError && /*#__PURE__*/ _jsxs("div", {
                className: "border-l-2 border-red-300 pl-2 text-xs text-red-700 space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: commitError
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: handleCommit,
                        className: "text-xs font-medium text-red-700 bg-red-100 rounded-md px-2 py-1 hover:bg-red-200",
                        children: "Retry"
                    })
                ]
            }),
            committed ? /*#__PURE__*/ _jsx("div", {
                className: "text-xs font-medium text-green-700",
                children: "Committed — see the commit result for this workstream for details."
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.commit,
                        disabled: readyCount === 0 || commit.isPending,
                        onClick: handleCommit,
                        className: "px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed",
                        children: commit.isPending ? 'Committing…' : `Commit ${readyCount} ${label} Changes`
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.reviewApproved,
                        onClick: handleReviewApproved,
                        className: "text-xs font-medium text-gray-600 hover:text-gray-800",
                        children: "Review Approved Changes"
                    }),
                    pendingCount > 0 ? /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.returnToPending,
                        onClick: handleReturnToPending,
                        className: "text-xs font-medium text-gray-600 hover:text-gray-800",
                        children: [
                            "Return to Pending Change",
                            pendingCount === 1 ? '' : 's'
                        ]
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-gray-400",
                        children: "Nothing pending to return to."
                    })
                ]
            }),
            disabledReason && !committed && /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-gray-500",
                children: disabledReason
            })
        ]
    });
}
